<script setup lang="ts">
import type { MappedFormField } from '~/models/Content/Form'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, useTemplateRef } from 'vue'

const {
  field,
  showLabel,
  showPlaceholder,
  showDescription,
  disableFields,
  maxWords = 0,
} = defineProps<{
  field: MappedFormField
  showLabel: boolean
  showPlaceholder: boolean
  showDescription: boolean
  disableFields: boolean
  maxWords?: number
}>()

const model = defineModel<string>()
const textareaEl = useTemplateRef('textarea')
const { t } = useI18n()

const label = computed(() => showLabel && field.name ? field.name : '')
const description = computed(() => showDescription && field.help ? field.help : '')
const placeholder = computed(() => showPlaceholder && field.placeholder ? field.placeholder : '')
// Split text by whitespace (/\s+/) and count only items containing at least one letter character (/\p{L}/u matches any Unicode letter)
const wordCount = computed(() => model.value?.trim().split(/\s+/).filter(word => /\p{L}/u.test(word)).length ?? 0)
const exceedsLimit = computed(() => maxWords > 0 && wordCount.value > maxWords)

function resize() {
  if (CSS.supports('field-sizing', 'content') || !textareaEl.value)
    return

  const el = textareaEl.value
  el.rows = 0

  while (el.scrollHeight !== el.clientHeight) {
    el.rows++

    if (el.rows > 20)
      break
  }
}

onMounted(() => resize())
</script>

<template>
  <div class="flex flex-col">
    <label
      v-if="label"
      :for="`input-${field.id}`"
      class="animate-fade-in-right text-[1em] font-bold"
      :class="{ 'mb-1': !description }"
      v-text="label"
    />
    <small
      v-if="description"
      :id="`description-${field.id}`"
      class="animate-fade-in-right mb-2 text-[.8em] font-normal"
      v-text="description"
    />
    <input
      v-if="field.identifier === 'single_line'"
      :id="`input-${field.id}`"
      v-model="model"
      :placeholder
      type="text"
      :aria-describedby="`description-${field.id}`"
      class="placeholder:text-gray-40 w-full rounded-lg border border-(--theme-50) bg-white px-4 py-3 ring-(--theme-50) transition hover:ring-1 focus-visible:ring-2 disabled:cursor-not-allowed"
      :disabled="disableFields"
    >
    <template v-else-if="field.identifier === 'multi_line'">
      <textarea
        :id="`input-${field.id}`"
        ref="textarea"
        v-model="model"
        :placeholder
        :aria-describedby="`description-${field.id}`"
        class="placeholder:text-gray-40 box-border min-h-[100px] w-full resize-y rounded-lg bg-white px-4 py-3 transition hover:ring-1 focus-visible:ring-2 disabled:cursor-not-allowed"
        :class="exceedsLimit ? 'border-2 border-[#F00] outline-2 outline-[#F00]' : 'border border-(--theme-50) ring-(--theme-50)'"
        :disabled="disableFields"
        @input="resize"
      />
      <p
        v-if="maxWords && wordCount"
        class="mt-1 text-right text-sm"
        :class="exceedsLimit ? 'text-[#F00] font-bold' : 'text-gray-400'"
        v-text="exceedsLimit ? t('formWriter.wordLimitReached', { max: maxWords }) : t('formWriter.wordCount', { count: wordCount })"
      />
    </template>
  </div>
</template>

<style scoped>
textarea {
  field-sizing: content;
}
</style>
